import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, Polygon, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { type UploadedKml, type KmlPoligono } from '../../services/kml/kmlService';

// ===============================
// INTERFACES
// ===============================

interface MapaPreviewProps {
  kmls: UploadedKml[];
  height?: string;
  showToggle?: boolean;
  className?: string;
  onPolygonClick?: (poligono: KmlPoligono, kmlIndex: number) => void;
  onLineClick?: (linea: KmlLinea, kmlIndex: number) => void; // ✅ NUEVO
}

// ✅ NUEVO: Interface para líneas
interface KmlLinea {
  id: string | number;
  nombre: string;
  descripcion?: string;
  longitud_metros: number;
  longitud_km?: number;
  geometria?: string;
  centroide?: string;
  calidad_geometria?: number;
  es_valido: boolean;
  orden: number;
  tipo_geometria?: string;
  metadatos_originales?: any;
  coordinates?: [number, number][]; // Para datos temporales
}

interface PoligonoMapa {
  poligono: KmlPoligono;
  kmlIndex: number;
  kmlNombre: string;
  color: string;
  coordinates: google.maps.LatLngLiteral[];
}

// ✅ NUEVO: Interface para líneas en el mapa
interface LineaMapa {
  linea: KmlLinea;
  kmlIndex: number;
  kmlNombre: string;
  color: string;
  coordinates: google.maps.LatLngLiteral[];
}

// ===============================
// CONFIGURACIÓN
// ===============================

const libraries: Array<'places' | 'geometry'> = ['places', 'geometry'];

const COLORES_KML = [
  '#3B82F6', // blue-500 - Polígonos
  '#EF4444', // red-500 - Polígonos
  '#10B981', // emerald-500 - Polígonos
  '#F59E0B', // amber-500 - Polígonos
  '#8B5CF6', // violet-500 - Polígonos
  '#EC4899', // pink-500 - Polígonos
  '#06B6D4', // cyan-500 - Polígonos
  '#84CC16', // lime-500 - Polígonos
  '#F97316', // orange-500 - Polígonos
  '#6366F1', // indigo-500 - Polígonos
];

// ✅ NUEVO: Colores específicos para líneas (más oscuros/vibrantes)
const COLORES_LINEAS = [
  '#1E40AF', // blue-800 - Más oscuro que polígonos
  '#DC2626', // red-600
  '#059669', // emerald-600  
  '#D97706', // amber-600
  '#7C3AED', // violet-600
  '#DB2777', // pink-600
  '#0891B2', // cyan-600
  '#65A30D', // lime-600
  '#EA580C', // orange-600
  '#4F46E5', // indigo-600
];

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: -33.4489,
  lng: -70.6693
};

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const MapaPreview: React.FC<MapaPreviewProps> = ({
  kmls,
  height = '400px',
  showToggle = true,
  className = '',
  onPolygonClick,
  onLineClick // ✅ NUEVO
}) => {
  const { t } = useTranslation();
  // Estados
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [poligonosMapa, setPoligonosMapa] = useState<PoligonoMapa[]>([]);
  const [lineasMapa, setLineasMapa] = useState<LineaMapa[]>([]); // ✅ NUEVO
  const [map, setMap] = useState<google.maps.Map | null>(null);

  // Hook de carga de Google Maps con múltiples fuentes para API Key (desarrollo y producción)
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY_OVERRIDE as string) || 
                 (window as any).__GOOGLE_MAPS_API_KEY__ ||
                 import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 
                 'AIzaSyCsgVgYItZbc_7aFCoPZDoFc8eaU-08d64'; // Fallback final
                 
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries,
    version: 'weekly'
  });

  // Opciones del mapa simplificadas
  const mapOptions = {
    mapTypeId: 'satellite',
    mapTypeControl: true,
    streetViewControl: false,
    fullscreenControl: true,
    zoomControl: true
  };

  // ===============================
  // CARGAR POLÍGONOS Y LÍNEAS
  // ===============================

  useEffect(() => {
    if (!mostrarMapa || !isLoaded || kmls.length === 0) return;

    const cargarGeometrias = async () => {
      const nuevosPoligonos: PoligonoMapa[] = [];
      const nuevasLineas: LineaMapa[] = []; // ✅ NUEVO

      try {
        for (let i = 0; i < kmls.length; i++) {
          const kml = kmls[i];
          
          // Para KMLs temporales (datos ya procesados)
          if (kml.poligonos && Array.isArray(kml.poligonos)) {
            // Procesar polígonos
            kml.poligonos.forEach((poligonoData: any, index: number) => {
              if (poligonoData.coordinates && Array.isArray(poligonoData.coordinates)) {
                const coordinates = poligonoData.coordinates.map((coord: [number, number]) => ({
                  lat: coord[1],
                  lng: coord[0]
                }));

                const poligono: KmlPoligono = {
                  id: `temp_${kml.archivo_id}_${index}`,
                  nombre: poligonoData.nombre || `Polígono ${index + 1}`,
                  descripcion: poligonoData.descripcion,
                  area_hectareas: poligonoData.area_hectareas,
                  area_metros2: poligonoData.area_hectareas * 10000,
                  perimetro_metros: poligonoData.perimetro_metros,
                  geometria: '',
                  centroide: '',
                  calidad_geometria: 1,
                  es_valido: true,
                  tiene_huecos: false,
                  orden: poligonoData.orden || index + 1,
                };

                nuevosPoligonos.push({
                  poligono,
                  kmlIndex: i,
                  kmlNombre: kml.nombre_original,
                  color: COLORES_KML[i % COLORES_KML.length],
                  coordinates
                });
              }
            });
          }
          
          // ✅ NUEVO: Procesar líneas temporales
          if (kml.lineas && Array.isArray(kml.lineas)) {
            kml.lineas.forEach((lineaData: any, index: number) => {
              if (lineaData.coordinates && Array.isArray(lineaData.coordinates)) {
                const coordinates = lineaData.coordinates.map((coord: [number, number]) => ({
                  lat: coord[1],
                  lng: coord[0]
                }));

                const linea: KmlLinea = {
                  id: `temp_line_${kml.archivo_id}_${index}`,
                  nombre: lineaData.nombre || `Línea ${index + 1}`,
                  descripcion: lineaData.descripcion,
                  longitud_metros: lineaData.longitud_metros,
                  longitud_km: lineaData.longitud_metros / 1000,
                  es_valido: true,
                  orden: lineaData.orden || index + 1,
                  tipo_geometria: 'LINESTRING',
                  coordinates: lineaData.coordinates
                };

                nuevasLineas.push({
                  linea,
                  kmlIndex: i,
                  kmlNombre: kml.nombre_original,
                  color: COLORES_LINEAS[i % COLORES_LINEAS.length], // ✅ Colores diferentes
                  coordinates
                });
              }
            });
          }
        }

        setPoligonosMapa(nuevosPoligonos);
        setLineasMapa(nuevasLineas); // ✅ NUEVO
        
      } catch (error) {
        console.error('Error cargando geometrías:', error);
        toast.error('Error cargando geometrías para el mapa');
      }
    };

    cargarGeometrias();
  }, [mostrarMapa, isLoaded, kmls]);

  // ===============================
  // CALLBACKS
  // ===============================

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Auto-zoom cuando se cargan polígonos Y líneas
  useEffect(() => {
    if (map && (poligonosMapa.length > 0 || lineasMapa.length > 0)) {
      const bounds = new google.maps.LatLngBounds();
      
      // Incluir polígonos en bounds
      poligonosMapa.forEach(({ coordinates }) => {
        coordinates.forEach(coord => {
          bounds.extend(coord);
        });
      });
      
      // ✅ NUEVO: Incluir líneas en bounds
      lineasMapa.forEach(({ coordinates }) => {
        coordinates.forEach(coord => {
          bounds.extend(coord);
        });
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
        
        // Limitar zoom máximo
        google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
          if (map.getZoom()! > 18) {
            map.setZoom(18);
          }
        });
      }
    }
  }, [map, poligonosMapa, lineasMapa]); // ✅ NUEVO: Incluir lineasMapa

  // ===============================
  // RENDER
  // ===============================

  if (kmls.length === 0) {
    return null;
  }

  if (loadError) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-red-800 font-medium mb-2">❌ Error cargando Google Maps</h3>
        <p className="text-red-700 text-sm">
          Error: {loadError.message}
        </p>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Toggle para mostrar/ocultar mapa */}
      {showToggle && (
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">🗺️ {t('kml.map_preview')}</h4>
          <button
            onClick={() => setMostrarMapa(!mostrarMapa)}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              mostrarMapa
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {mostrarMapa ? `🙈 ${t('kml.hide_map')}` : `👁️ ${t('kml.show_map')}`}
          </button>
        </div>
      )}

      {/* Contenedor del mapa */}
      {mostrarMapa && (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
          {/* Header con información */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                📁 {kmls.length} archivo{kmls.length !== 1 ? 's' : ''} KML
                {poligonosMapa.length > 0 && ` • ${poligonosMapa.length} polígono${poligonosMapa.length !== 1 ? 's' : ''}`}
                {lineasMapa.length > 0 && ` • ${lineasMapa.length} línea${lineasMapa.length !== 1 ? 's' : ''}`} {/* ✅ NUEVO */}
              </div>
              
              {!isLoaded && (
                <div className="flex items-center space-x-2 text-blue-600">
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="text-sm">{t('common.loading')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Mapa */}
          <div style={{ height }}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={defaultCenter}
                zoom={10}
                options={mapOptions}
                onLoad={onLoad}
                onUnmount={onUnmount}
              >
                {/* Polígonos */}
                {poligonosMapa.map((poligonoMapa, index) => (
                  <Polygon
                    key={`${poligonoMapa.kmlIndex}-polygon-${index}`}
                    paths={poligonoMapa.coordinates}
                    options={{
                      strokeColor: poligonoMapa.color,
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      fillColor: poligonoMapa.color,
                      fillOpacity: 0.35,
                      clickable: true
                    }}
                    onClick={() => {
                      if (onPolygonClick) {
                        onPolygonClick(poligonoMapa.poligono, poligonoMapa.kmlIndex);
                      } else {
                        toast(`📍 ${poligonoMapa.poligono.nombre}\n🏞️ Área: ${poligonoMapa.poligono.area_hectareas} ha`, {
                          duration: 3000,
                          icon: '🗺️'
                        });
                      }
                    }}
                  />
                ))}
                
                {/* ✅ NUEVO: Líneas */}
                {lineasMapa.map((lineaMapa, index) => (
                  <Polyline
                    key={`${lineaMapa.kmlIndex}-line-${index}`}
                    path={lineaMapa.coordinates}
                    options={{
                      strokeColor: lineaMapa.color,
                      strokeOpacity: 0.9,
                      strokeWeight: 4,
                      clickable: true
                    }}
                    onClick={() => {
                      if (onLineClick) {
                        onLineClick(lineaMapa.linea, lineaMapa.kmlIndex);
                      } else {
                        toast(`📍 ${lineaMapa.linea.nombre}\n📏 Longitud: ${lineaMapa.linea.longitud_metros.toFixed(2)} m (${lineaMapa.linea.longitud_km?.toFixed(2)} km)`, {
                          duration: 3000,
                          icon: '📏'
                        });
                      }
                    }}
                  />
                ))}
              </GoogleMap>
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-50">
                <div className="text-center">
                  <div className="text-gray-400 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </div>
                  <p className="text-gray-500">{t('common.loading')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Leyenda de colores */}
          {(poligonosMapa.length > 0 || lineasMapa.length > 0) && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <h5 className="text-xs font-medium text-gray-700 mb-2">Leyenda:</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Mostrar polígonos */}
                {Array.from(new Set(poligonosMapa.map(p => p.kmlIndex))).map(kmlIndex => {
                  const kml = kmls[kmlIndex];
                  const color = COLORES_KML[kmlIndex % COLORES_KML.length];
                  const poligonosDeEsteKml = poligonosMapa.filter(p => p.kmlIndex === kmlIndex);
                  const areaTotal = poligonosDeEsteKml.reduce((sum, p) => sum + p.poligono.area_hectareas, 0);

                  return (
                    <div key={`polygon-${kmlIndex}`} className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded border border-gray-300"
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-xs text-gray-600 truncate">
                        🔺 {kml.nombre_original} ({areaTotal.toFixed(2)} ha)
                      </span>
                    </div>
                  );
                })}
                
                {/* ✅ NUEVO: Mostrar líneas */}
                {Array.from(new Set(lineasMapa.map(l => l.kmlIndex))).map(kmlIndex => {
                  const kml = kmls[kmlIndex];
                  const color = COLORES_LINEAS[kmlIndex % COLORES_LINEAS.length];
                  const lineasDeEsteKml = lineasMapa.filter(l => l.kmlIndex === kmlIndex);
                  const longitudTotal = lineasDeEsteKml.reduce((sum, l) => sum + l.linea.longitud_metros, 0);

                  return (
                    <div key={`line-${kmlIndex}`} className="flex items-center space-x-2">
                      <div className="flex items-center">
                        <div 
                          className="w-6 h-1 border-gray-300"
                          style={{ backgroundColor: color }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 truncate">
                        📏 {kml.nombre_original} ({(longitudTotal/1000).toFixed(2)} km)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapaPreview;